import type { ReactNode } from "react";
import { Link } from "react-router";
import { Image as ImageIcon } from "lucide-react";
import type { ListingCard } from "../../api/catalogue";
import { Icon } from "../ui";
import { cx } from "../ui/cx";
import { OriginBadge, PriceTag, StockBadge } from "./partLabels";

/** A part's photo on white, or a calm placeholder: a part added after launch may not have one yet. */
export function ListingPhoto({ photo, alt, className }: { photo: ListingCard["photo"]; alt: string; className?: string }) {
  return (
    <span className={cx("flex items-center justify-center overflow-hidden bg-white", className)}>
      {photo ? (
        <img src={photo.url} alt={alt} loading="lazy" className="size-full object-contain" />
      ) : (
        <span className="flex flex-col items-center gap-2 px-3 text-center text-text-muted">
          <Icon icon={ImageIcon} size={24} />
          <span className="type-eyebrow text-[10px] tracking-[var(--tracking-wider)] uppercase">Not photographed yet</span>
        </span>
      )}
    </span>
  );
}

export interface ProductCardProps {
  listing: ListingCard;
  /** A line under the name, like the machines it fits. */
  detail?: ReactNode;
  /** Below the card's link: an add-to-basket button. */
  footer?: ReactNode;
  className?: string;
}

/** Catalogue tile (design system ProductCard): square photo, the number on the part, name, price and stock. */
export function ProductCard({ listing, detail, footer, className }: ProductCardProps) {
  return (
    <div className={cx("flex flex-col overflow-hidden rounded-lg border border-border-subtle bg-surface-card shadow-sm hover:border-green-300 hover:shadow-md", className)}>
      <Link to={`/listings/${listing.id}`} className="flex flex-1 flex-col border-b-0 text-inherit">
        <span className="relative block">
          <ListingPhoto photo={listing.photo} alt={listing.name} className="aspect-square border-b border-border-subtle" />
          {listing.origin === "genuine" && (
            <span className="absolute top-2.5 left-2.5">
              <OriginBadge origin={listing.origin} />
            </span>
          )}
        </span>
        <span className="flex flex-1 flex-col gap-2 p-4">
          <span className="type-sku [font-size:var(--text-xs)] [overflow-wrap:anywhere] text-text-muted">{listing.reference_number ?? listing.sku}</span>
          <span className="type-h4 [font-size:var(--text-base)] leading-snug text-text-strong">{listing.name}</span>
          {detail && <span className="type-small text-text-muted">{detail}</span>}
          {listing.replaced_by && <span className="type-small text-warning-700">Replaced by {listing.replaced_by.sku}</span>}
          <span className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2">
            <PriceTag cents={listing.price_cents} unit={listing.unit} size="sm" />
            <StockBadge inStock={listing.in_stock} />
          </span>
        </span>
      </Link>
      {footer && <div className="px-4 pb-4">{footer}</div>}
    </div>
  );
}
